import { useEffect, useState } from "react";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  Button,
  Callout,
  Checkbox,
  Spinner,
  Icon,
} from "@blueprintjs/core";

const ACK_KEY = "criterion_credits_acknowledged";

export interface RevealTarget {
  full_name: string | null;
  title?: string | null;
  company_name?: string | null;
  photo_url?: string | null;
}

interface Props {
  isOpen: boolean;
  target: RevealTarget | null;
  revealType: "email" | "phone";
  estimatedCredits: number;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function CreditConfirmDialog({
  isOpen,
  target,
  revealType,
  estimatedCredits,
  loading,
  onCancel,
  onConfirm,
}: Props) {
  const [acknowledged, setAcknowledged] = useState<boolean>(() => {
    return localStorage.getItem(ACK_KEY) === "true";
  });
  const [neverShowFullWarning, setNeverShowFullWarning] = useState(acknowledged);

  // Refresh from storage whenever the dialog (re)opens, in case another tab updated it.
  useEffect(() => {
    if (isOpen) {
      setAcknowledged(localStorage.getItem(ACK_KEY) === "true");
    }
  }, [isOpen]);

  // Keyboard: Esc → cancel, Enter → confirm (when not loading).
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !loading) {
        e.preventDefault();
        onConfirm();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, loading, onConfirm]);

  const handleConfirm = () => {
    if (neverShowFullWarning && !acknowledged) {
      localStorage.setItem(ACK_KEY, "true");
      setAcknowledged(true);
    }
    onConfirm();
  };

  const label = revealType === "email" ? "an email address" : "a phone number";
  const labelTitle = revealType === "email" ? "Reveal Email" : "Reveal Phone";
  const showFullWarning = !acknowledged;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={loading ? undefined : onCancel}
      title={labelTitle}
      icon={revealType === "email" ? "envelope" : "phone"}
      canEscapeKeyClose={!loading}
      canOutsideClickClose={!loading}
      isCloseButtonShown={!loading}
      style={{ width: 480 }}
    >
      <DialogBody>
        {target && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 12px",
              borderRadius: 6,
              background: "#f5f8fa",
              marginBottom: 16,
            }}
          >
            {target.photo_url ? (
              <img
                src={target.photo_url}
                alt=""
                style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
              />
            ) : (
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#137cbd",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {(target.full_name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {target.full_name || "Unknown contact"}
              </div>
              <div style={{ fontSize: 12, color: "#5c7080", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {[target.title, target.company_name].filter(Boolean).join(" • ") || "—"}
              </div>
            </div>
          </div>
        )}

        <Callout intent="warning" icon="dollar" title={`This will use ~${estimatedCredits} credit${estimatedCredits === 1 ? "" : "s"}`}>
          <div style={{ marginTop: 4 }}>
            Each successful reveal of verified contact data uses credits. You're about to reveal {label} for this contact.
          </div>
        </Callout>

        {showFullWarning && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              border: "1px solid #d9e0e6",
              background: "#fafbfc",
              fontSize: 13,
              lineHeight: 1.5,
              color: "#394b59",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, color: "#1c2127" }}>
              <Icon icon="info-sign" size={13} style={{ marginRight: 6, color: "#137cbd" }} />
              Be mindful of credit usage
            </div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>Email reveal: <strong>1 credit</strong> per verified contact</li>
              <li>Phone reveal: <strong>5 credits</strong> per verified number</li>
              <li>You're only charged when the data is successfully verified — failed reveals are free</li>
              <li>Contacts already revealed in the last 30 days are cached and don't cost credits</li>
            </ul>
          </div>
        )}

        <div style={{ marginTop: 14, display: "flex", alignItems: "center" }}>
          <Checkbox
            checked={neverShowFullWarning}
            onChange={(e) => setNeverShowFullWarning((e.target as HTMLInputElement).checked)}
            label="I understand — don't show the full warning again"
            disabled={loading}
            style={{ marginBottom: 0 }}
          />
        </div>
      </DialogBody>

      <DialogFooter
        actions={
          <>
            <Button onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              intent="primary"
              onClick={handleConfirm}
              disabled={loading}
              icon={loading ? undefined : (revealType === "email" ? "envelope" : "phone")}
            >
              {loading ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Spinner size={14} intent="primary" />
                  Revealing…
                </span>
              ) : (
                <>Reveal {revealType === "email" ? "Email" : "Phone"} · {estimatedCredits} credit{estimatedCredits === 1 ? "" : "s"}</>
              )}
            </Button>
          </>
        }
      />
    </Dialog>
  );
}
